import { useTranslation } from 'react-i18next'

import {
  FLOATING_ELEMENT_PLACEMENT,
  NOTIFICATION_TYPE,
} from '../../../../../../constants/common'
import { useContentOverflow } from '../../../../../../hooks'
import { FloatingTooltip } from '../../../../../Floating'
import { ICON_COLOR, ICON_HEIGHT_16, IconNG } from '../../../../../Icon'
import { NotificationInline } from '../../../../../Notification'
import {
  getApplicantCreditCheckBandProps,
  getIsApplicantFieldMasked,
} from '../../../../../RentalPortalApplicant'
import { Status, STATUS_SIZE_20 } from '../../../../../Status'
import Text from '../../../../../Text'
import { RentalPortalKeyFactsWidgetItem } from '../../../../RentalPortalKeyFactsWidgetItem'
import {
  APPLICANT_CREDIT_CHECK_PROVIDER_TEXT_COLOR,
  getIsApplicantCreditCheckInconclusive,
  getIsApplicantCreditCheckResultExpired,
} from '../../..'
import {
  QA_NAME_APPLICANT_CREDIT_CHECK_BAND,
  QA_NAME_APPLICANT_CREDIT_CHECK_EXPIRED,
  QA_NAME_APPLICANT_CREDIT_CHECK_PROVIDER,
} from '../ApplicantCreditCheckBlock.constants'
import {
  getApplicantCreditCheckDateRows,
  getApplicantCreditCheckNativeValueRow,
  getApplicantCreditCheckVerificationRows,
} from '../ApplicantCreditCheckBlock.utils'

import * as styles from '../ApplicantCreditCheckBlock.module.css'

import DocumentIcon from '../../../../../../icons/document.svg'

function ApplicantCreditCheckResult({ canViewSensitive, result }) {
  const { t } = useTranslation()

  const { overflow, setContentOverflowRef } = useContentOverflow()

  const isMasked = getIsApplicantFieldMasked(result.normalised_band)

  const isExpired = getIsApplicantCreditCheckResultExpired(result)

  const nativeValueRow = getApplicantCreditCheckNativeValueRow(result)

  return (
    <div className={styles.resultGroup}>
      <div className={styles.resultHead}>
        <FloatingTooltip
          content={result.provider_display_name}
          disabled={!overflow}
          placement={FLOATING_ELEMENT_PLACEMENT.top}
        >
          <Text
            className={styles.provider}
            qaName={QA_NAME_APPLICANT_CREDIT_CHECK_PROVIDER}
            ref={setContentOverflowRef}
          >
            {result.provider_display_name}
          </Text>
        </FloatingTooltip>
        {isMasked || result.normalised_band?.value ? (
          <Status
            {...getApplicantCreditCheckBandProps({
              field: result.normalised_band,
              t,
            })}
            height={STATUS_SIZE_20}
            qaName={QA_NAME_APPLICANT_CREDIT_CHECK_BAND}
            tooltip={
              isMasked
                ? t('RentalPortal.ApplicantCreditCheckMaskedHintKey')
                : null
            }
          />
        ) : (
          <Text isSecondary>
            {t('RentalPortal.ApplicantCreditCheckNoVerdictKey')}
          </Text>
        )}
        {isExpired ? (
          <Status
            height={STATUS_SIZE_20}
            qaName={QA_NAME_APPLICANT_CREDIT_CHECK_EXPIRED}
            text={t('RentalPortal.ApplicantCreditCheckExpiredChipKey')}
            textColor={APPLICANT_CREDIT_CHECK_PROVIDER_TEXT_COLOR}
          />
        ) : null}
      </div>

      <div className={styles.resultRows}>
        {nativeValueRow ? (
          <RentalPortalKeyFactsWidgetItem
            label={nativeValueRow.label}
            value={nativeValueRow.value}
          />
        ) : null}
        {canViewSensitive && !isMasked ? (
          <Text isSecondary className={styles.rowNote}>
            {t('RentalPortal.ApplicantCreditCheckSensitiveGrantKey')}
          </Text>
        ) : null}
        {getApplicantCreditCheckDateRows({ result, t }).map(row => (
          <RentalPortalKeyFactsWidgetItem
            key={row.key}
            label={row.label}
            value={row.value}
          />
        ))}
        {result.verification_code_supported
          ? getApplicantCreditCheckVerificationRows({ result, t }).map(row => (
              <RentalPortalKeyFactsWidgetItem
                key={row.key}
                label={row.label}
                value={row.value}
              />
            ))
          : null}
        {isExpired ? (
          <Text isSecondary className={styles.rowNote}>
            {t('RentalPortal.ApplicantCreditCheckExpiredHintKey')}
          </Text>
        ) : null}
        {result.document_id ? (
          <>
            <Text isSecondary className={styles.rowLabel}>
              {t('RentalPortal.ApplicantCreditCheckVerdictDocumentLabelKey')}
            </Text>
            <span className={styles.document}>
              <IconNG
                Icon={DocumentIcon}
                color={ICON_COLOR.SecondaryTextColor}
                size={ICON_HEIGHT_16}
              />
              <Text className={styles.documentLabel}>
                {t('RentalPortal.ApplicantCreditCheckDocumentKey')}
              </Text>
            </span>
          </>
        ) : null}
      </div>

      {getIsApplicantCreditCheckInconclusive(result) ? (
        <span className={styles.resultSpan}>
          <NotificationInline
            text={t('RentalPortal.ApplicantCreditCheckNoDataHeldKey', {
              provider: result.provider_display_name,
            })}
            type={NOTIFICATION_TYPE.info}
          >
            <Text isSecondary className={styles.line}>
              {t('RentalPortal.ApplicantCreditCheckNoDataHeldHintKey')}
            </Text>
          </NotificationInline>
        </span>
      ) : null}
    </div>
  )
}

export default ApplicantCreditCheckResult
